// PROFILE-AUTH — intent carry-over for the profile interaction gates.
//
// A signed-out visitor on /p/<slug> sees a read-only profile: every
// interaction (rate, comment, bookmark, DM, report) opens a sign-up gate.
// Rather than lose what they were doing, we stash their intent at the
// moment the gate blocks it and restore it once they're signed in. This
// widened the old single-purpose comment stash (lib/pendingComment.ts)
// when the rating verdict itself became auth-gated.
//
// localStorage, not sessionStorage: the auth detour can leave the origin
// entirely (Apple's form_post lands in AppleCallback via a real top-level
// bounce) and may span a tab close or a "come back later" gap. Same
// rationale as lib/attribution.ts and lib/referral.ts.
//
// Deliberately SINGLE-SLOT and last-write-wins. Someone who drafts on card
// A, wanders to card B and drafts there, then signs up should get B back —
// the restore is keyed on slug, so a stale A intent simply never matches.
//
// Restoration NEVER auto-submits. The rate composer gets its selections
// back and the visitor presses Rate; the bookmark gets a "tap to save"
// hint; the report modal reopens as a blank-but-open form. One confirming
// tap, always.

const KEY_PENDING_INTENT = 'wydm.pending_profile_intent';

/**
 * How long a stashed intent stays valid. Generous on purpose: the signup
 * detour is email verify → builder → publish, which a real person can
 * easily spread over an evening. Much shorter loses genuine drafts; much
 * longer starts restoring text the author has forgotten writing.
 */
const TTL_MS = 24 * 60 * 60 * 1000;

/** Mirrors MAX_NOTE_LEN in convex/functions/ratings.ts. */
const NOTE_MAX = 240;

/**
 * What the visitor was trying to do when the gate stopped them.
 *
 *   rate     — verdict and/or comment draft; restored into the composer.
 *   bookmark — save-to-Keepers tap; restored as a hint by the button.
 *   dm       — start a conversation; no payload, the DM button is right
 *              there when they land back.
 *   report   — report the profile; restored by reopening the (blank)
 *              report form.
 */
export type ProfileIntent =
  | { kind: 'rate'; verdict: 'yes' | 'no' | null; note: string; anonymous?: boolean }
  | { kind: 'bookmark' }
  | { kind: 'dm' }
  | { kind: 'report' };

export interface PendingProfileIntent {
  slug: string;
  intent: ProfileIntent;
  savedAt: number;
}

/**
 * Stash an intent. Call this ONLY at the point an interaction is actually
 * blocked by the gate — not on unmount, blur, or beforeunload. An intent
 * the visitor never expressed carries no meaning, and stashing those is
 * what produces stale state that later ambushes an unrelated signed-in
 * user of the same browser.
 *
 * A 'rate' intent with neither a verdict nor a note is dropped (nothing
 * worth carrying).
 */
export function stashProfileIntent(slug: string, intent: ProfileIntent): void {
  const cleanSlug = slug.trim().toLowerCase();
  if (!cleanSlug) return;
  let clean: ProfileIntent = intent;
  if (intent.kind === 'rate') {
    const note = intent.note.trim().slice(0, NOTE_MAX);
    if (!note && !intent.verdict) return;
    clean = { ...intent, note };
  }
  try {
    const payload: PendingProfileIntent = {
      slug: cleanSlug.slice(0, 64),
      intent: clean,
      savedAt: Date.now(),
    };
    localStorage.setItem(KEY_PENDING_INTENT, JSON.stringify(payload));
  } catch {
    // Private mode / quota exceeded. The stash is a nicety, never a
    // correctness requirement — the gate itself still works.
  }
}

const KINDS = new Set(['rate', 'bookmark', 'dm', 'report']);

function read(): PendingProfileIntent | undefined {
  try {
    const raw = localStorage.getItem(KEY_PENDING_INTENT);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as PendingProfileIntent;
    if (!parsed || typeof parsed !== 'object') return undefined;
    if (typeof parsed.slug !== 'string') return undefined;
    if (typeof parsed.savedAt !== 'number') return undefined;
    const intent = parsed.intent as ProfileIntent | undefined;
    if (!intent || typeof intent !== 'object' || !KINDS.has(intent.kind)) {
      return undefined;
    }
    if (intent.kind === 'rate' && typeof intent.note !== 'string') {
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
}

/**
 * Read without clearing, dropping (and deleting) anything past its TTL.
 * Use this to decide where to send someone after auth, where consuming the
 * intent would be wrong — the card that actually restores it hasn't
 * rendered yet.
 */
export function peekProfileIntent(): PendingProfileIntent | undefined {
  const parsed = read();
  if (!parsed) return undefined;
  if (Date.now() - parsed.savedAt > TTL_MS) {
    clearProfileIntent();
    return undefined;
  }
  return parsed;
}

/**
 * Read and clear. Single-use by design: once an intent has been applied it
 * must not be able to reappear for a different user of the same browser.
 */
export function takeProfileIntent(): PendingProfileIntent | undefined {
  const parsed = peekProfileIntent();
  if (parsed) clearProfileIntent();
  return parsed;
}

/**
 * Drop any stashed intent. Called on sign-out, which closes the shared-
 * device hole: visitor A abandons a draft, visitor B signs in on the same
 * browser and would otherwise find A's intent restored under B's name.
 */
export function clearProfileIntent(): void {
  try {
    localStorage.removeItem(KEY_PENDING_INTENT);
  } catch {
    // Nothing useful to do — see stashProfileIntent.
  }
}

/**
 * Where to send someone straight after they authenticate.
 *
 * Returns the card they were interacting with when a live intent is
 * waiting, so the restore is immediate instead of requiring them to find
 * the link again. Peeks rather than takes — the card consumes the intent
 * on render.
 *
 * `profileComplete` gates it deliberately: a brand-new user still owes the
 * builder/onboarding funnel, and short-circuiting that to deliver a rating
 * would trade a bigger win for a smaller one. Their intent keeps for 24h.
 * Because of this gate the call is safe to make from shared signup/signin
 * code — a genuine new account has profileComplete false and falls back on
 * its own. Defaults to true for callers that don't know (a plain sign-in),
 * where an existing account is implied.
 *
 * Rating/commenting does NOT itself require a published profile, so
 * landing an incomplete-profile signer-in on the card is still coherent.
 */
export function postAuthDestination(
  fallback: string,
  profileComplete = true,
): string {
  if (!profileComplete) return fallback;
  const pending = peekProfileIntent();
  if (!pending) return fallback;
  return `/p/${pending.slug}`;
}
